import { useEffect, useRef } from "react";
import { type FromApp, fromApp, isInAppAgent, type ToApp } from "../../shared/inApp";

/** Inside the Flashsuite phone app: nothing that sells is drawn, and the app keeps one screen per tab. */
export const IN_APP = typeof navigator !== "undefined" && isInAppAgent(navigator.userAgent);

interface AppWindow {
  ReactNativeWebView?: { postMessage(message: string): void };
}

/** Tell the phone app something; outside it, nothing happens. */
export function postToApp(message: ToApp): void {
  (window as Window & AppWindow).ReactNativeWebView?.postMessage(JSON.stringify(message));
}

let readyTold = false;

/**
 * Tell the phone app, once per page load, that the first real screen is drawn: it shows the page then, in place
 * of its own mark (APP-MODE.md section 10).
 */
export function tellAppReady(): void {
  if (readyTold || !IN_APP) return;
  readyTold = true;
  postToApp({ v: 1, kind: "ready" });
}

/** Hear what the phone app tells this page. Returns the way to stop. */
export function onFromApp(heard: (message: FromApp) => void): () => void {
  const listen = (e: Event) => {
    const m = fromApp((e as CustomEvent).detail);
    if (m) heard(m);
  };
  window.addEventListener("flashsuite-app", listen);
  return () => window.removeEventListener("flashsuite-app", listen);
}

/**
 * Read again what this screen shows when the phone app shows it again: every tab's screen is its own page, so
 * a change made on another one reaches it only by reading again (APP-MODE.md section 4). Quiet by design: the
 * caller keeps showing what it has while the new answer comes. Outside the app it never runs.
 */
export function useOnShown(read: () => void): void {
  const latest = useRef(read);
  latest.current = read;
  useEffect(() => {
    if (!IN_APP) return;
    return onFromApp((m) => {
      if (m.kind === "shown") latest.current();
    });
  }, []);
}
